/**
 * Rectangle - Axis-aligned rectangle with point references
 *
 * Like walls reference points (startPointId, endPointId), rectangles
 * can reference 4 corner points. When the points move, the rectangle
 * moves with them.
 */

import { GeometryType } from '../base/GeometryBase';
import { Vector } from '../base/Vector';
import { Polyline } from './Polyline';

export class Rectangle extends Polyline {
  /**
   * Create a rectangle from two corners or point IDs
   * @param {Vector|{x,y,z}} corner1 - First corner (or null if using pointIds)
   * @param {Vector|{x,y,z}} corner2 - Opposite corner (or null if using pointIds)
   * @param {Object} attributes - Including optional pointIds array
   */
  constructor(corner1, corner2, attributes = {}) {
    // If pointIds are provided, we'll set points later from external state
    const pointIds = attributes.pointIds || null;

    let c1, c2, points;

    if (corner1 && corner2) {
      c1 = corner1 instanceof Vector ? corner1 : Vector.fromObject(corner1);
      c2 = corner2 instanceof Vector ? corner2 : Vector.fromObject(corner2);

      // Create 4 corners (counter-clockwise)
      points = [
        new Vector(c1.x, c1.y, c1.z),
        new Vector(c2.x, c1.y, c1.z),
        new Vector(c2.x, c2.y, c1.z),
        new Vector(c1.x, c2.y, c1.z),
      ];
    } else {
      // Empty points - will be filled from external point state
      c1 = new Vector(0, 0, 0);
      c2 = new Vector(0, 0, 0);
      points = [
        new Vector(0, 0, 0),
        new Vector(0, 0, 0),
        new Vector(0, 0, 0),
        new Vector(0, 0, 0),
      ];
    }

    super(points, true, attributes); // closed = true

    // Override type
    this.type = GeometryType.RECTANGLE;
    this.id = this.id.replace('pl_', 'rect_');

    // Store original corners for reference (when not using pointIds)
    this._corner1 = c1;
    this._corner2 = c2;

    // Store point IDs for reference-based rectangles
    // Order: [bottomLeft, bottomRight, topRight, topLeft]
    this._pointIds = pointIds;
  }

  /**
   * Get point IDs (if this rectangle references external points)
   */
  get pointIds() {
    return this._pointIds;
  }

  /**
   * Set point IDs
   */
  set pointIds(ids) {
    this._pointIds = ids;
    this.touch();
  }

  /**
   * Check if this rectangle uses point references
   */
  get usesPointReferences() {
    return this._pointIds && this._pointIds.length === 4;
  }

  /**
   * Update internal points from external point state
   * @param {Function} getPoint - Function to get point by ID (returns {x, y, z})
   */
  updateFromPoints(getPoint) {
    if (!this.usesPointReferences) return;

    const points = this._pointIds.map(id => {
      const pt = getPoint(id);
      return pt ? new Vector(pt.x, pt.y, pt.z || 0) : new Vector(0, 0, 0);
    });

    this._points = points;

    // Update corner references
    if (points.length >= 4) {
      this._corner1 = points[0];
      this._corner2 = points[2]; // Diagonal corner
    }
  }

  /**
   * Get corner positions (from points if using references, otherwise from internal)
   * @param {Function} getPoint - Optional function to get point by ID
   * @returns {Vector[]} Array of 4 corner vectors
   */
  getCorners(getPoint = null) {
    if (this.usesPointReferences && getPoint) {
      return this._pointIds.map(id => {
        const pt = getPoint(id);
        return pt ? new Vector(pt.x, pt.y, pt.z || 0) : new Vector(0, 0, 0);
      });
    }
    return this._points;
  }

  /**
   * Create rectangle from center and size
   * @param {Vector|{x,y,z}} center
   * @param {number} width
   * @param {number} height
   * @param {Object} attributes
   * @returns {Rectangle}
   */
  static fromCenterSize(center, width, height, attributes = {}) {
    const c = center instanceof Vector ? center : Vector.fromObject(center);
    const halfW = width / 2;
    const halfH = height / 2;
    return new Rectangle(
      { x: c.x - halfW, y: c.y - halfH, z: c.z },
      { x: c.x + halfW, y: c.y + halfH, z: c.z },
      attributes
    );
  }

  /**
   * Create rectangle from origin and size
   * @param {Vector|{x,y,z}} origin - Bottom-left corner
   * @param {number} width
   * @param {number} height
   * @param {Object} attributes
   * @returns {Rectangle}
   */
  static fromOriginSize(origin, width, height, attributes = {}) {
    const o = origin instanceof Vector ? origin : Vector.fromObject(origin);
    return new Rectangle(
      o,
      { x: o.x + width, y: o.y + height, z: o.z },
      attributes
    );
  }

  get width() {
    return Math.abs(this._corner2.x - this._corner1.x);
  }

  get height() {
    return Math.abs(this._corner2.y - this._corner1.y);
  }

  get center() {
    return new Vector(
      (this._corner1.x + this._corner2.x) / 2,
      (this._corner1.y + this._corner2.y) / 2,
      this._corner1.z
    );
  }

  get area() {
    return this.width * this.height;
  }

  get perimeter() {
    return 2 * (this.width + this.height);
  }

  /**
   * Check if point is inside rectangle
   * @param {Vector|{x,y,z}} point
   * @returns {boolean}
   */
  containsPoint(point) {
    const p = point instanceof Vector ? point : Vector.fromObject(point);
    const minX = Math.min(this._corner1.x, this._corner2.x);
    const maxX = Math.max(this._corner1.x, this._corner2.x);
    const minY = Math.min(this._corner1.y, this._corner2.y);
    const maxY = Math.max(this._corner1.y, this._corner2.y);

    return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
  }

  clone() {
    const rect = new Rectangle(this._corner1.clone(), this._corner2.clone(), { ...this.attributes });
    rect._pointIds = this._pointIds ? [...this._pointIds] : null;
    return rect;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      type: GeometryType.RECTANGLE,
      corner1: this._corner1.toObject(),
      corner2: this._corner2.toObject(),
      pointIds: this._pointIds || null,
    };
  }

  static fromJSON(json) {
    const rect = new Rectangle(
      Vector.fromObject(json.corner1),
      Vector.fromObject(json.corner2),
      { ...json.attributes, pointIds: json.pointIds || null }
    );
    rect.id = json.id;
    rect.createdAt = json.createdAt;
    rect.modifiedAt = json.modifiedAt;
    return rect;
  }
}

export default Rectangle;
